import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { AccordionDetails, AccordionSummary } from "@mui/material";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useParams } from "react-router-dom";
import { getProjectSettingsPageUrl } from "@Features/projects/utils/projectUrls";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";

interface StatusDetailProps {
  settingContent: string;
  setSettingContent: (value: string) => void;
}

interface HandleSettingsTabChangeParams {
  settingsValue: string;
  extraSearchParams?: Record<string, string>;
}

function StatusDetail({ settingContent, setSettingContent }: StatusDetailProps) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const navigate = useNavigate();
  const params = useParams<{ projectId: string }>();
  const userAdminHeader = () => (
    <div className="text-sm font-semibold">
      <Text>{t("User Administration")}</Text>
    </div>
  );
  const generalInformationHeader = () => (
    <div className="text-sm font-semibold">
      <Text>{t("General")}</Text>
    </div>
  );
  const configHeader = () => (
    <div className="text-sm font-semibold">
      <Text>{t("Configuration")}</Text>
    </div>
  );
  const handleSettingsTabChange = ({ settingsValue, extraSearchParams = {} }: HandleSettingsTabChangeParams) => {
    if (params.projectId) {
      const url = getProjectSettingsPageUrl({
        projectId: params.projectId,
        tab: settingsValue,
        extraSearchParams,
      });
      navigate(url, { replace: true });
      setSettingContent(settingsValue);
    }
  };
  const userAdminContent = () => {
    const isDpmsPortal = window.location.host.includes("dpmsportal");
    return (
      <div>
        {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.PROJECT_USERS?.ENABLE && (
          <div
            tabIndex={1}
            className={`flex p-3 ${settingContent === "users" ? "bg-[rgb(209,235,255)]" : ""}`}
            data-testid="projectUsersWrapper"
          >
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "users" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSideBarProjectUsers"
            >
              <Text>{t("Project Users")}</Text>
            </div>
          </div>
        )}
        {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.PROJECT_USERS?.ENABLE && !isDpmsPortal && (
          <div
            tabIndex={1}
            className={`flex p-3 ${settingContent === "groups" ? "bg-[rgb(209,235,255)]" : ""}`}
            data-testid="projectGroupsWrapper"
          >
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "groups" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSideBarProjectGroups"
            >
              <Text>{t("Groups")}</Text>
            </div>
          </div>
        )}
        {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.PROJECT_SPACES?.ENABLE && (
          <div
            className={`flex p-3 ${settingContent === "spaces" ? "bg-[rgb(209,235,255)]" : ""}`}
            data-testid="projectSpacesWrapper"
          >
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "spaces" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSideBarProjectSpaces"
            >
              <Text>{t("Project Spaces")}</Text>
            </div>
          </div>
        )}
        {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.PROJECT_ROLES?.ENABLE && (
          <div
            className={`flex p-3 ${settingContent === "roles" ? "bg-[rgb(209,235,255)]" : ""}`}
            data-testid="projectRolesWrapper"
          >
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "roles" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSideBarRoles"
            >
              <Text>{t("Roles")}</Text>
            </div>
          </div>
        )}
        {!isDpmsPortal && (
          <div
            className={`flex p-3 ${settingContent === "teams" ? "bg-[rgb(209,235,255)]" : ""}`}
            data-testid="projectTeamsWrapper"
          >
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "teams" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSideBarTeams"
            >
              <Text>{t("Teams")}</Text>
            </div>
          </div>
        )}
      </div>
    );
  };
  const configContent = () => (
    <div>
      <div className={`flex p-3 ${settingContent === "currency" ? "bg-[rgb(209,235,255)]" : ""}`}>
        <div
          onClick={() => handleSettingsTabChange({ settingsValue: "currency" })}
          className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
          data-testid="leftSideBarConfigurationCurrency"
        >
          <Text>{t("Currency")}</Text>
        </div>
      </div>
      {/* <div className={`flex p-3 ${settingContent === "resource_type" ? "bg-[rgb(209,235,255)]" : ""}`}>
        <div
          onClick={() => handleSettingsTabChange({ settingsValue: "resource_type" })}
          className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
          data-testid="leftSideBarConfigurationResourceType"
        >
          <Text>{t("Resource Type")}</Text>
        </div>
      </div> */}
      <div className={`flex p-3 ${settingContent === "projectModules" ? "bg-[rgb(209,235,255)]" : ""}`}>
        <div
          onClick={() => handleSettingsTabChange({ settingsValue: "projectModules" })}
          className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
          data-testid="leftSideBarConfigurationProjectModules"
        >
          <Text>{t("Modules")}</Text>
        </div>
      </div>
      <div className={`flex p-3 ${settingContent === "inspection_location" ? "bg-[rgb(209,235,255)]" : ""}`}>
        <div
          onClick={() => handleSettingsTabChange({ settingsValue: "inspection_location" })}
          className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
          data-testid="leftSideBarConfigurationLocation"
        >
          <Text>{t("Location")}</Text>
        </div>
      </div>
    </div>
  );
  const generalContent = () => {
    const isDpmsPortal = window.location.host.includes("dpmsportal");
    return (
      <>
        <div className={`flex p-3 ${settingContent === "general_information" ? "bg-[rgb(209,235,255)]" : ""}`}>
          <div
            onClick={() => handleSettingsTabChange({ settingsValue: "general_information" })}
            className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
            data-testid="leftSidebarProjectInformation"
          >
            <Text>{t("Project Information")}</Text>
          </div>
        </div>
        {!isDpmsPortal && (
          <div className={`flex p-3 ${settingContent === "WBS" ? "bg-[rgb(209,235,255)]" : ""}`}>
            <div
              onClick={() => handleSettingsTabChange({ settingsValue: "WBS" })}
              className="flex-[2] cursor-pointer text-sm text-[rgba(0,0,0,0.54)] focus:text-[rgb(240,0,0)] active:text-[rgb(240,0,0)]"
              data-testid="leftSidebarWBS"
            >
              <Text>{t("WBS")}</Text>
            </div>
          </div>
        )}
      </>
    );
  };
  return (
    <div className="ms-0.5 h-full w-[28%] overflow-auto pe-2">
      {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.ENABLE && (
        <Accordion defaultExpanded className="bg-transparent" style={{ boxShadow: "none", margin: "0" }}>
          <AccordionSummary style={{ padding: "0" }} expandIcon={<ExpandMoreIcon />}>
            {generalInformationHeader()}
          </AccordionSummary>
          <AccordionDetails>{generalContent()}</AccordionDetails>
        </Accordion>
      )}
      {layout.PROJECT_SETTINGS?.USER_ADMINSTRATION?.ENABLE && (
        <Accordion defaultExpanded className="bg-transparent" style={{ boxShadow: "none", margin: "0" }}>
          <AccordionSummary style={{ padding: "0" }} expandIcon={<ExpandMoreIcon />}>
            {userAdminHeader()}
          </AccordionSummary>
          <AccordionDetails>{userAdminContent()}</AccordionDetails>
        </Accordion>
      )}
      {layout.PROJECT_SETTINGS?.CONFIGURATION?.ENABLE && (
        <Accordion defaultExpanded className="bg-transparent" style={{ boxShadow: "none", margin: "0" }}>
          <AccordionSummary style={{ padding: "0" }} expandIcon={<ExpandMoreIcon />}>
            {configHeader()}
          </AccordionSummary>
          <AccordionDetails>{configContent()}</AccordionDetails>
        </Accordion>
      )}
    </div>
  );
}
export default StatusDetail;
